import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams, NavLink } from 'react-router-dom';
import 'antd/dist/antd.css';
import { Col, Row, Button, Space, Card, Typography, message } from 'antd';
import DocumentHistory from './DocumentHistory';
import ShowPdf from '../../components/pdf/ShowPdf';
import ShowAssets from './ShowAssets';
import ModalDemurContract from '../../components/modals/ModalDemurContract';
import ModalSendNotarisa from '../../components/modals/ModalSendNotarisa';
import ModalCancelContract from '../../components/modals/ModalCancelContract';
import {
  GET_CONTRACT_DATA_FORM,
  GET_CONTRACT_PDF,
  RESET_IS_LOADED,
  DEMUR_CONTRACT,
  CONTRACT_IS_DEMUR,
  FAILURE,
  SEND_NOTARISA,
  IS_SENT_NOTARISA,
  CONTRACT_IS_CANCELED,
  CANCEL_CONTRACT,
  GET_DOMAIN_CERT
} from '../../state/lease_contract/types';
import { RESOURCE } from '../../config/constants/alerts';
import ViewSkeleton from '../../components/detail_contract/ContractSkeleton';
import { ROLES, STATES } from '../../config/constants/contractStates';
import ReSendMail from '../../components/mails/ReSendMail';
import ContractInfo from './ContractInfo';
import TransactionInfo from './TransactionInfo';
import ShowCertificate from './ShowCertificates';
import PropTypes from 'prop-types';
import ContractEditedWarning from '../../components/detail_contract/ContractEditedWarning';
import UploadPdf from '../../components/detail_contract/UploadPdf';
import DownloadPdf from '../../components/pdf/DownloadPdf';
import DomainCert from '../../components/detail_contract/DomainCert';

const { Title } = Typography;

/**
 * Component that returns a View with the detail of selected contract and shows all the action that can be performed on it.
 * @component
 * @returns {Element} View with contract detail.
 */
const ContractDetail = ({
  getContract,
  isLoaded,
  contract,
  getPDF,
  contractPdf,
  resetIsLoaded,
  sendDemurContract,
  contractIsDemur,
  resetDemurState,
  operationFailure,
  sendNotarisa,
  contractIsSent,
  resetSentNotariza,
  resetFailure,
  cancelContract,
  isCanceled,
  getDomainCert,
}) => {

  const { params } = useParams();

  const [loaderSpinning, setLoaderSpinning] = useState(true);
  const [demurVisible, setDemurVisible] = useState(false);
  const [sendNotarisaVisible, setSendNotarisaVisible] = useState(false);
  const [cancelVisible, setCancelVisible] = useState(false);
  const [modalSpinning, setModalSpinning] = useState(false);
  const [logMsg] = useState('Ocurrió un error en el proceso de este contrato. Por favor contacte con el área técnica.');

  const getData = () => {
    getContract(params);
    getPDF(params);
  };

  useEffect(() => {
    if (params) {
      getData();
    }
  }, [params]);

  useEffect(() => { 
    if (contract.inmueble) {
      getDomainCert(contract.inmueble.certificadoDominio);
    }
  }, [contract]);

  useEffect(() => {
    if (isLoaded) {
      setLoaderSpinning(false);
      resetIsLoaded();
    };
  }, [isLoaded]);

  useEffect(() => {
    if (contractIsDemur) {
      getData();
      setModalSpinning(false);
      setDemurVisible(false);
      message.success('Contrato fue objetado', 10);
      resetDemurState();
    }
    if (contractIsSent) {
      getData();
      setModalSpinning(false);
      setSendNotarisaVisible(false);
      message.success('Contrato fue enviado', 10);
      resetSentNotariza();
    }
    if (isCanceled) {
      getData();
      setModalSpinning(false);
      setCancelVisible(false);
      message.success('Contrato fue anulado', 10);
      resetSentNotariza();
    }
    if (operationFailure) {
      setModalSpinning(false);
      setDemurVisible(false);
      setSendNotarisaVisible(false);
      setCancelVisible(false);
      resetFailure();
    }
  }, [contractIsDemur, operationFailure, contractIsSent, isCanceled]);

  const demurContract = (msg) => {
    setModalSpinning(true);
    sendDemurContract(contract._id, { rol: ROLES.ADMIN }, msg);
  };

  const sendContractNotarisa = () => {
    setModalSpinning(true);
    sendNotarisa(contract._id);
  };

  const sendCancelContract = () => {
    setModalSpinning(true);
    cancelContract(contract._id);
  };

  const contractWasSent = contract.history?.map(entry => entry.estado).includes('signedByAll');

  return (
    <React.Fragment>
      {loaderSpinning
        ? (<ViewSkeleton />)
        : (<>
          <React.Fragment>
            <Row gutter={16}>
              <Col sm={24} md={12} className="mb-4">
                <ContractInfo />
                <TransactionInfo />
                {contract.estado !== STATES.DRAFT &&
                  <Card>
                    <Row>
                      <Col sm={10} className="d-flex">
                        <Space>
                          <ReSendMail />
                        </Space>
                      </Col>
                      <Col sm={14} className="d-flex justify-content-end">
                        <Space>
                          {![STATES.FINALIZED, STATES.OBJECTED, STATES.AWAITING_SIGN, STATES.AWAITING_NOTARY, STATES.SENDED].includes(contract.estado) &&
                            <Button
                              type="default" danger
                              onClick={() => setDemurVisible(true)}
                            >
                              Objetar
                            </Button>
                          }
                          {![STATES.VALIDATE_BY_ALL, STATES.AWAITING_SIGN, STATES.AWAITING_NOTARY, STATES.SENDED, STATES.FINALIZED].includes(contract.estado) && !contract.isPdfModified &&
                            <NavLink to={`/contracts/admin/edit_contract/${contract._id}`}>
                              <Button>
                                Editar
                              </Button>
                            </NavLink>
                          }
                          {![STATES.SENDED, STATES.AWAITING_SIGN, STATES.AWAITING_NOTARY, STATES.FINALIZED].includes(contract.estado) &&
                            <Button
                              type="default" danger
                              onClick={() => setCancelVisible(true)}
                            >
                              Anular contrato
                            </Button>
                          }
                          {contract.estado === STATES.SENDED || (contractWasSent && contract.estado === STATES.HALT) &&
                            <Button
                              type="primary"
                              onClick={() => setSendNotarisaVisible(true)}
                            >
                              Reenviar contrato
                            </Button>
                          }
                        </Space>
                      </Col>
                    </Row>
                  </Card>
                }
                {(contract.estado && contract.estado === STATES.HALT) &&
                  <Card>
                    <Row>
                      <Col>
                        <Row>
                          <Title level={4} ellipsis> Resumen de error: </Title>
                        </Row>
                        <Row>
                          {logMsg}
                        </Row>
                      </Col>
                    </Row>
                  </Card>
                }
                <Card>
                  <Row>
                    <DocumentHistory />
                  </Row>
                </Card>
              </Col>
              <Col sm={24} md={12}>
                <ContractEditedWarning />
                <ShowPdf pdf={contractPdf} />
                <DownloadPdf contractId={contract?.contractId} />
                <Row className="justify-content-center">
                  {![STATES.VALIDATE_BY_ALL, STATES.AWAITING_SIGN, STATES.AWAITING_NOTARY, STATES.SENDED, STATES.FINALIZED].includes(contract.estado) &&
                    <UploadPdf id={params} />
                  }
                </Row>
                <ShowAssets />
                <ShowCertificate />
                <DomainCert />
              </Col>
            </Row>
          </React.Fragment>
          <ModalDemurContract
            visible={demurVisible}
            onCancel={() => setDemurVisible(false)}
            onCreate={demurContract}
            spinning={modalSpinning}
          />
          <ModalSendNotarisa
            visible={sendNotarisaVisible}
            onCancel={() => setSendNotarisaVisible(false)}
            onCreate={sendContractNotarisa}
            spinning={modalSpinning}
          />
          <ModalCancelContract
            visible={cancelVisible}
            onCancel={() => setCancelVisible(false)}
            onCreate={sendCancelContract}
            spinning={modalSpinning}
          />
        </>
        )}
    </React.Fragment>
  );
};

ContractDetail.propTypes = {
  isLoaded: PropTypes.bool.isRequired,
  getContract: PropTypes.func.isRequired,
  contract: PropTypes.object.isRequired,
  getPDF: PropTypes.func.isRequired,
  getDomainCert: PropTypes.func.isRequired,
  contractPdf: PropTypes.string.isRequired,
  resetIsLoaded: PropTypes.func.isRequired,
  sendDemurContract: PropTypes.func.isRequired,
  contractIsDemur: PropTypes.bool.isRequired,
  resetDemurState: PropTypes.func.isRequired,
  operationFailure: PropTypes.bool.isRequired,
  sendNotarisa: PropTypes.func.isRequired,
  contractIsSent: PropTypes.bool.isRequired,
  resetSentNotariza: PropTypes.func.isRequired,
  resetFailure: PropTypes.func.isRequired,
  cancelContract: PropTypes.func.isRequired,
  isCanceled: PropTypes.bool.isRequired,
};

const data = (state) => ({
  contract: state.contract.contract,
  isLoaded: state.contract.isLoaded,
  contractPdf: state.contract.contractPdf,
  contractIsDemur: state.contract.contractIsDemur,
  contractIsSent: state.contract.contractIsSent,
  operationFailure: state.contract.operationFailure,
  isCanceled: state.contract.isCanceled
});

const action = (dispatch) => ({
  getContract: (id) => dispatch({ type: GET_CONTRACT_DATA_FORM, payload: { id, resource: RESOURCE.GET_CONTRACT } }),
  getPDF: (id) => dispatch({ type: GET_CONTRACT_PDF, payload: { id, resource: RESOURCE.GET_CONTRACT_PDF } }),
  getDomainCert: (id) => dispatch({ type: GET_DOMAIN_CERT, payload: { id, resource: RESOURCE.GET_DOMAIN_CERT } }),
  resetIsLoaded: () => dispatch({ type: RESET_IS_LOADED }),
  sendDemurContract: (id, rol, msg) => dispatch({ type: DEMUR_CONTRACT, payload: { id, rol, msg, resource: RESOURCE.DEMUR_CONTRACT } }),
  resetDemurState: () => dispatch({ type: CONTRACT_IS_DEMUR, payload: false }),
  resetSentNotariza: () => dispatch({ type: IS_SENT_NOTARISA, payload: false }),
  resetFailure: () => dispatch({ type: FAILURE, payload: false }),
  sendNotarisa: (id) => dispatch({ type: SEND_NOTARISA, payload: { id, resource: RESOURCE.SEND_NOTARISA } }),
  cancelContract: (id) => dispatch({ type: CANCEL_CONTRACT, payload: { id, resource: RESOURCE.CANCEL_CONTRACT } }),
  resetCancelState: () => dispatch({ type: CONTRACT_IS_CANCELED, payload: false }),
});

export default connect(data, action)(ContractDetail);